import {
  IMaconomyProject,
  IMemberDepricated,
  IProjectDeprecated,
  IProjectMcBimSettings,
  IUserActiveItem,
} from "@mctypes/global.types";
import { IAzureBlobFile } from "@schemas/azureSchema";
import {
  ICapitalLetter,
  IOtherAttributeConfigItemProps,
  IOtherAttributesConfigLean,
} from "@schemas/otherAttributesSchema";
import { apiSlice } from "../apiSlice";
import { setShowSuccessMessageThunk } from "../global/globalSlice";

export const projectRTK = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createOrJoinProject: builder.mutation<IProjectDeprecated, IMaconomyProject>({
      query: (input) => ({
        url: `/user/project`,
        body: {
          maconomy: input,
        },
        method: "PUT",
      }),
      invalidatesTags: ["Project"],
    }),
    getProjectMembers: builder.query<IMemberDepricated[], string>({
      query: (projectId) => ({
        url: `/project/${projectId}/members`,
        method: "GET",
      }),
    }),
    getProject: builder.query<IProjectDeprecated, string>({
      query: (projectId) => ({
        url: `/project/${projectId}/single`,
        method: "GET",
      }),
      providesTags: ["Project"],
    }),
    enableMcBim: builder.mutation<IProjectMcBimSettings, string>({
      query: (projectId) => ({
        url: `/project/${projectId}/enable-mcbim`,
        method: "POST",
      }),
      invalidatesTags: ["Project"],
    }),
    getActiveAssignedItems: builder.query<IUserActiveItem[], { userId: string; projectId: string }>({
      query: (input) => ({
        url: `/project/${input.projectId}/active-asigned-items/${input.userId}`,
        method: "GET",
      }),
      providesTags: ["Issue"],
    }),
    getActiveUserOwnedItems: builder.query<IUserActiveItem[], { userId: string; projectId: string }>({
      query: (input) => ({
        url: `/project/${input.projectId}/user-owned-items/${input.userId}`,
        method: "GET",
      }),
      providesTags: ["Issue"],
    }),
    uploadMeetingDocx: builder.mutation<void, { projectId: string; meetingId: string; event: any }>({
      query: ({ projectId, meetingId, event }) => {
        const formData = new FormData();
        formData.append("fileToUpload", event.target.files[0]);
        return {
          url: `/project/${projectId}/meeting-docx-template`,
          method: "POST",
          body: formData,
        };
      },
    }),
    getProjectMeetingDocxTemplates: builder.query<IAzureBlobFile[], { projectId: string }>({
      query: (input) => ({
        url: `/project/${input.projectId}/meeting-docx-templates`,
        method: "GET",
      }),
      providesTags: ["Meeting"],
    }),
    deleteMeetingDocxTemplate: builder.mutation<void, { projectId: string; fullBlobId: string }>({
      query: ({ projectId, fullBlobId }) => ({
        url: `/project/${projectId}/delete-meeting-template`,
        body: {
          fullBlobId: fullBlobId,
        },
        method: "DELETE",
      }),
      invalidatesTags: ["Meeting"],
    }),
    uploadIssueboardDocx: builder.mutation<void, { projectId: string; boardId: string; event: any }>({
      query: ({ projectId, boardId, event }) => {
        const formData = new FormData();
        formData.append("fileToUpload", event.target.files[0]);
        formData.append("boardId", boardId);
        return {
          url: `/project/${projectId}/issueboard-docx-template.add/${boardId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["IssueBoard"],
    }),
    getProjectIssueboardDocxTemplates: builder.query<IAzureBlobFile[], { projectId: string; issueBoardId: string }>({
      query: (input) => ({
        url: `/project/${input.projectId}/issueboard-docx-templates.get`,
        method: "POST",
        body: {
          boardId: input.issueBoardId,
        },
      }),
      providesTags: ["IssueBoard"],
    }),
    getProjectIssueDocxTemplates: builder.query<IAzureBlobFile[], { projectId: string }>({
      query: (input) => ({
        url: `/project/${input.projectId}/issue-docx-templates.get`,
        method: "POST",
      }),
      providesTags: ["IssueBoard"],
    }),
    deleteProjectIssueDocxTemplate: builder.mutation<void, { projectId: string; fullBlobId: string }>({
      query: ({ projectId, fullBlobId }) => ({
        url: `/project/${projectId}/issuedocx.delete`,
        body: {
          fullBlobId: fullBlobId,
        },
        method: "POST",
      }),
      invalidatesTags: ["IssueBoard"],
    }),
    getProjectLogoTypeAzureBlobStorageFile: builder.query<IAzureBlobFile | null, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/logotype.get`,
        method: "POST",
      }),
      providesTags: ["Project"],
    }),
    deleteProjectLogo: builder.mutation<string | null, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/logotype.delete`,
        method: "POST",
      }),
      invalidatesTags: ["Project"],
    }),
    uploadLogoType: builder.mutation<void, { projectId: string; image: File }>({
      query: ({ projectId, image }) => {
        const formData = new FormData();
        formData.append("fileToUpload", image);
        return {
          url: `/project/${projectId}/logotype.add`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Project"],
    }),
    updateUserName: builder.mutation<{ firstName: string; lastName: string }, { firstName: string; lastName: string }>({
      query: (newUsername) => ({
        url: "/user/update-name",
        method: "POST",
        body: newUsername,
      }),
    }),
    pingMCappsAzureFunctions: builder.query<void, void>({
      query: () => ({
        url: "/mcapps-azure-function-ping",
        method: "POST",
      }),
    }),
    addMemberOtherAttribute: builder.mutation<
      void,
      {
        projectId: string;
        body: { memberOtherAttributesConfigItemProps: IOtherAttributeConfigItemProps; key?: ICapitalLetter };
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/project/${projectId}/member-other-attributes-config`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Project"],
    }),
    updateMemberOtherAttribute: builder.mutation<
      void,
      {
        projectId: string;
        body: { key: ICapitalLetter; memberOtherAttributesConfigItemProps: IOtherAttributeConfigItemProps };
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/member-other-attributes-config`,
          method: "PUT",
          body: input.body,
        };
      },
      invalidatesTags: ["Project"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Medlemsattributt er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateMemberOtherAttributesOrder: builder.mutation<
      IOtherAttributesConfigLean,
      { projectId: string; idsInOrder: ICapitalLetter[] }
    >({
      query: ({ projectId, idsInOrder }) => ({
        url: `/project/${projectId}/member-other-attributes-config/order`,
        method: "PUT",
        body: { idsInOrder },
      }),
      invalidatesTags: ["Project"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Rekkefølge oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useGetProjectQuery,
  useEnableMcBimMutation,
  useGetProjectMeetingDocxTemplatesQuery,
  useDeleteMeetingDocxTemplateMutation,
  useGetProjectLogoTypeAzureBlobStorageFileQuery,
  useDeleteProjectLogoMutation,
  useUploadLogoTypeMutation,
  useUploadIssueboardDocxMutation,
  useGetProjectIssueboardDocxTemplatesQuery,
  useGetProjectIssueDocxTemplatesQuery,
  useDeleteProjectIssueDocxTemplateMutation,
  usePingMCappsAzureFunctionsQuery,
  useCreateOrJoinProjectMutation,
  useAddMemberOtherAttributeMutation,
  useUpdateMemberOtherAttributeMutation,
  useUpdateMemberOtherAttributesOrderMutation,
} = projectRTK;
